import React, { useState } from 'react';
import { STATUS, FONT_MONO, statusToSemantic, statusLabel } from 'Utils/styleTokens';
import { explainJobError } from 'Utils/errorText';
import { ErrorDetailsDialog } from 'Utils/ErrorDetails';
import StepsTable, { pairWithLatestRun, stepStatusLabel } from './StepsTable';
import { typeLabel } from './groupTypes';
import { fmtDateTime } from 'Utils/formatDate';
import { useCanEdit, READ_ONLY_TITLE } from 'Auth/permissions';

// A run whose steps all share (source, inventory) renders fan-style in the collapsed
// header: "source · inventory → dest-chips" (or "— in place"). Mixed multi-source runs
// keep the plain description line and the ordered steps table tells their story.
const fanShapeOf = (group) => {
  const steps = group.steps || [];
  if (steps.length === 0) return null;
  const first = steps[0];
  const homogeneous = steps.every(
    (s) => s.dataSourceId === first.dataSourceId && s.inventoryId === first.inventoryId
  );
  if (!homogeneous) return null;
  return {
    sourceName: first.dataSourceName || `#${first.dataSourceId}`,
    inventoryName: first.inventoryName || (first.inventoryId != null ? `#${first.inventoryId}` : ''),
    inPlace: steps.every((s) => s.targetDataSourceId == null),
  };
};

const fmtWhen = (iso) => fmtDateTime(iso);

// Group-level pill: latest run's status, or Idle when the group never ran.
const groupStatus = (group) => {
  if (!group.latestRun) return { label: statusLabel('IDLE'), semantic: 'neutral' };
  const s = group.latestRun.status;
  return { label: statusLabel(s), semantic: statusToSemantic(s) };
};

function GroupCard({
  group,
  expanded,
  onToggle,
  onRun,
  onCancel,
  onDelete,
  onEdit,
  onReplaceSteps,
  onAddStep,
  onEditStep,
}) {
  const [showError, setShowError] = useState(false);
  const canEdit = useCanEdit();
  const failure =
    group.latestRun && group.latestRun.status === 'FAILED' && group.latestRun.errorMessage
      ? explainJobError(group.latestRun.errorMessage)
      : null;
  const status = groupStatus(group);
  const pill = STATUS[status.semantic] || STATUS.neutral;
  const stepCount = (group.steps || []).length;
  const metaCell = (label, value) => (
    <div className='text-right'>
      <div
        className='sg-small'
        style={{ color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '.08em', fontSize: 11, fontWeight: 600 }}
      >
        {label}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>{value}</div>
    </div>
  );

  return (
    <div
      className='sg-card mb-3'
      style={{ padding: 0, overflow: 'hidden', borderColor: expanded ? 'var(--brand-blue, #315DFF)' : undefined }}
      data-testid={`job-group-${group.id}`}
    >
      <div
        role='button'
        aria-expanded={expanded}
        onClick={onToggle}
        className='d-flex align-items-center'
        style={{ gap: 14, padding: '14px 20px', cursor: 'pointer' }}
      >
        <span className='sg-small' style={{ color: 'var(--ink-500)', width: 14 }}>
          {expanded ? '▾' : '▸'}
        </span>
        <div style={{ minWidth: 0 }}>
          <div className='d-flex align-items-center' style={{ gap: 10 }}>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink-900)' }}>{group.name}</span>
            <span
              style={{
                fontFamily: FONT_MONO,
                fontSize: 11.5,
                color: 'var(--ink-500)',
                background: 'var(--ink-50)',
                border: '1px solid var(--ink-150)',
                borderRadius: 6,
                padding: '1px 7px',
              }}
            >
              grp-{group.id}
            </span>
            <span
              className='sg-pill sg-pill--nodot'
              data-testid={`job-group-type-${group.id}`}
              style={{
                background: 'var(--ink-50)',
                color: 'var(--ink-500)',
                border: '1px solid var(--ink-150)',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {typeLabel(group.typeKey)}
            </span>
          </div>
          {(() => {
            const fan = fanShapeOf(group);
            if (!fan) {
              return group.description ? (
                <div className='sg-small' style={{ color: 'var(--ink-500)', marginTop: 3 }}>
                  {group.description}
                </div>
              ) : null;
            }
            const paired = pairWithLatestRun(group.steps, group.latestRun);
            return (
              <div
                className='d-flex align-items-center flex-wrap'
                style={{ gap: 6, marginTop: 4 }}
                data-testid={`run-fan-${group.id}`}
              >
                <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: 'var(--ink-700)' }}>
                  {fan.sourceName}
                  {fan.inventoryName ? (
                    <span style={{ color: 'var(--ink-500)' }}> · {fan.inventoryName}</span>
                  ) : null}
                </span>
                {fan.inPlace ? (
                  <span className='sg-small' style={{ color: 'var(--ink-500)' }}>(in place)</span>
                ) : (
                  <>
                    <span style={{ color: 'var(--ink-500)' }}>→</span>
                    {paired.map(({ step, runStep }) => {
                      const st = stepStatusLabel(runStep, !!group.runActive);
                      const pillColors = STATUS[st.semantic] || STATUS.neutral;
                      return (
                        <span
                          key={step.id != null ? step.id : `${step.targetDataSourceId}`}
                          className='d-inline-flex align-items-center'
                          title={runStep && runStep.errorMessage ? `${st.label}: ${explainJobError(runStep.errorMessage).headline}` : st.label}
                          style={{
                            gap: 5,
                            fontFamily: FONT_MONO,
                            fontSize: 11,
                            color: 'var(--ink-700)',
                            background: 'var(--ink-50)',
                            border: '1px solid var(--ink-150)',
                            borderRadius: 6,
                            padding: '1px 8px',
                          }}
                        >
                          <span
                            style={{ width: 7, height: 7, borderRadius: '50%', background: pillColors.dot, flex: 'none' }}
                          />
                          {step.targetDataSourceName || `#${step.targetDataSourceId}`}
                        </span>
                      );
                    })}
                  </>
                )}
              </div>
            );
          })()}
          {/* The latest run's failure is visible on the collapsed card as a plain-language
              headline (never the raw exception — that lives behind Details, in full). */}
          {failure && (
            <div className='sg-small d-flex align-items-center flex-wrap' style={{ color: STATUS.danger.text, marginTop: 4, gap: 8 }} data-testid={`job-group-error-${group.id}`}>
              <span>{failure.headline}</span>
              <button
                type='button'
                className='btn btn-link btn-sm p-0'
                style={{ fontSize: 'inherit', color: STATUS.danger.text, textDecoration: 'underline' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowError(true);
                }}
                data-testid={`job-group-error-details-${group.id}`}
              >
                Details
              </button>
              <ErrorDetailsDialog
                show={showError}
                onHide={() => setShowError(false)}
                title={`${group.name}: latest run failed`}
                {...failure}
              />
            </div>
          )}
        </div>
        <div className='ml-auto d-flex align-items-center' style={{ gap: 22, flex: 'none' }}>
          {metaCell('Steps', <span style={{ fontFamily: FONT_MONO, fontWeight: 600 }}>{stepCount}</span>)}
          {metaCell('Schedule', 'Manual')}
          {metaCell('Last run', fmtWhen(group.latestRun && group.latestRun.startedAt))}
          <span
            className='sg-pill sg-pill--nodot'
            style={{ background: pill.bg, color: pill.text, fontSize: 11, fontWeight: 600 }}
            title={failure ? failure.headline : undefined}
          >
            {status.label}
          </span>
          {group.runActive ? (
            <button
              type='button'
              disabled={!canEdit}
              title={!canEdit ? READ_ONLY_TITLE : undefined}
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
              style={{
                border: '1px solid var(--ink-200)',
                background: '#fff',
                cursor: canEdit ? 'pointer' : 'not-allowed',
                fontSize: 11.5,
                fontWeight: 600,
                color: STATUS.danger.text,
                padding: '7px 14px',
                borderRadius: 7,
              }}
            >
              Cancel run
            </button>
          ) : (
            <button
              type='button'
              data-testid={`job-group-run-${group.id}`}
              disabled={!canEdit}
              title={!canEdit ? READ_ONLY_TITLE : undefined}
              onClick={(e) => {
                e.stopPropagation();
                onRun();
              }}
              style={{
                border: 'none',
                background: STATUS.info.bg,
                cursor: canEdit ? 'pointer' : 'not-allowed',
                fontSize: 11.5,
                fontWeight: 600,
                color: STATUS.info.text,
                padding: '7px 14px',
                borderRadius: 7,
              }}
            >
              ▶ Run group
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <StepsTable
          group={group}
          onReplaceSteps={onReplaceSteps}
          onAddStep={onAddStep}
          onEditStep={onEditStep}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}

export default GroupCard;
